import React, { useMemo, useRef } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
/** Redux */
import { useSelector } from 'react-redux';
import { extractModalStateFromAnalysis } from '../../../redux/selectors/analyses';
/** Mouse handlers hook */
/** Events handler */
import useIncidents from '../hooks/useIncidents';
/** Chart format hook */
import useYAxisFormat from '../hooks/useYAxisFormat';
/** CHART COMPONENTS */
import CustomYAxisTick from '../CustomYAxisTick';
import CustomXAxisTick from '../CustomXAxisTick';
import CustomTooltip from '../CustomTooltip';
/** UTILS */
import {
  BAR_CHART_MIN_POINT_SIZE,
  BAR_CHART_STROKE_WIDTH,
  CHART_MARGIN,
  DATA_KEY,
  DEFAULT_X_AXIS_CONFIG,
} from '../constants/utils';
import {
  useChartLines,
  useOthersLine,
  usePrepareCohort,
  useSingleChartDataTooltip,
  useYAxisBounds,
  useYAxisWidth,
} from '../hooks';
import { assertCondition } from '../../../utils/types';
import { QueryType } from '../../../utils/apollo/graphqlSchema';
import { bootstrapTimezoneSelector } from '../../../redux/selectors/bootstrap';
import { useAppSelector } from '../../../redux/hooks';
import ResponsiveChartWrapper from '../ResponsiveChartWrapper';
import useBuilderInfoDisplay from '../utils/useBuilderInfoDisplay';
import { mapComparisonKey } from '../../../utils/redux/mergeExpressionComparisonData';
import {
  ChartNumberType,
  ExpressionPostfixOnColumnName,
  YAxisPosition,
} from '../interfaces';
import { ChartMode } from '../types';
import calculateBarSize from '../utils/calculateBarSize';
import SingleChartDataTooltip from '../SingleChartDataTooltip';
import { isNil } from 'lodash';
import { useTooltipWheelContext } from '../CustomTooltip/CustomTooltipWheelContext';
import { useBarChartOpacity } from '../hooks/useBarChartOpacity';
import { isSkipPercentView } from '../utils';
import useCacheParsedChartLlmRawDataEffect from '../hooks/llmCacheRawData/useCacheParsedChartLlmRawDataEffect';
import { useFloatingLayerContext } from '../../FloatingManager';
import PopperPortal from '../CustomTooltip/PopperPortal';
import { useChartContext } from '../ChartContext';
import StyledBar from '../BarChart/StyledBar';
interface IProps {
  analysisId: string;
  mode: ChartMode;
}

export default React.memo(({ analysisId, mode }: IProps) => {
  const { tooltipContentRef } = useTooltipWheelContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const modalsStateMemoizedPropSelector = useMemo(
    extractModalStateFromAnalysis,
    [],
  );
  const {
    chartData,
    displayedSlices,
    settings: { chartSettings, chartWidth, incidents },
    metadata: { isBreakdownOverall, query },
    colorConfig,
    displayedMeasures,
  } = useChartContext();
  const { activeIncidentTypes, axisSettings } = chartSettings;
  assertCondition(chartData.queryType === QueryType.Expression);
  const skipPercentView = isSkipPercentView(query, chartData.measureControls);

  const { data } = chartData;
  const { chartType, chartNumberType } = chartSettings;

  const orgTimezone = useAppSelector(bootstrapTimezoneSelector);
  /* Chart modals state */
  const isCreateIncidentModalOpen = useSelector((state) =>
    modalsStateMemoizedPropSelector(
      state,
      analysisId,
      'isCreateIncidentModalOpen',
    ),
  );
  const createIncidentData = useSelector((state) =>
    modalsStateMemoizedPropSelector(state, analysisId, 'createIncidentData'),
  );

  const { visibleLines } = useChartLines({
    chartSettings,
    chartData,
    isBreakdownOverall,
    colorConfig,
  });

  const {
    onBarMouseMove,
    onMouseLeave,
    tooltipProps,
    clearLockedTooltipProps,
    lockTooltipPositionHandler,
    isTooltipLocked,
  } = useSingleChartDataTooltip({
    containerRef,
  });
  const { setActiveLabel, getBarOpacity } = useBarChartOpacity();
  const { handleCreateCohortFromExpression } = usePrepareCohort({
    analysisId,
  });

  // update linesMeta, visibleLines and data for others line
  const [
    linesWithOthers,
    linesMetaWithOthers,
    dataWithOthers,
    displayedSlicesWithOthers,
  ] = useOthersLine({
    // filter out the lines for non-measure line types, for example, ma7, ma30, etc.
    visibleLines,
    chartData,
    chartSettings,
    displayedSlices,
    chartNumberType,
    skipPercentView,
  });
  const currentFormat = useYAxisFormat({
    forcePercent: chartNumberType === ChartNumberType.Percentage,
  });

  const { getYAxisDomain } = useYAxisBounds();

  const chartDates = useMemo(
    () => data?.map((singleDataPoint) => singleDataPoint.date as string),
    [data],
  );

  const { renderIncidents, renderCreateIncidentReferenceLine } = useIncidents({
    analysisId,
    incidents,
    createIncidentData,
    isCreateIncidentModalOpen,
    chartDates,
    activeIncidentTypes,
    yAxisId: 0,
  });

  const builderInfoDisplay = useBuilderInfoDisplay(analysisId);

  const showMeasure =
    visibleLines.length === 1 ||
    (visibleLines.length === 2 &&
      visibleLines[1].name === mapComparisonKey(visibleLines[1].measureKey));

  const numItems = data.length;
  const barSize = calculateBarSize(numItems, chartWidth);

  const tooltipZIndex = useFloatingLayerContext();

  // XXX also cache the raw chart numbers in redux, to be used as a context sent to the LLM
  useCacheParsedChartLlmRawDataEffect({
    chartTypeName: 'Stacked Bar Chart',
    parsedChartDataRecords: dataWithOthers,
    analysisId,
  });

  // Calculate the Y axis width dynamically
  const yAxisWidth = useYAxisWidth();

  return (
    <ResponsiveChartWrapper analysisId={analysisId} mode={mode}>
      <ResponsiveContainer ref={containerRef}>
        <BarChart
          margin={CHART_MARGIN}
          data={dataWithOthers}
          barCategoryGap={'25%'}
          onMouseMove={(props) => {
            setActiveLabel(props.activeLabel ?? null);
          }}
          onMouseLeave={() => {
            setActiveLabel(null);
          }}
        >
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey={DATA_KEY}
            {...DEFAULT_X_AXIS_CONFIG}
            tick={({ ...props }) => (
              <CustomXAxisTick
                {...props}
                limit={barSize}
                shouldRotate={false}
              />
            )}
          />
          <YAxis
            padding={{ top: 3, bottom: 0 }}
            hide={!axisSettings.yAxisSettings[YAxisPosition.LEFT].isVisible}
            tickSize={0}
            domain={getYAxisDomain({
              yAxisPosition: YAxisPosition.LEFT,
              visibleLines,
            })}
            allowDataOverflow={true}
            axisLine={false} // Whether to display the Y axis vertical line or not
            tick={<CustomYAxisTick measureDisplayFormat={currentFormat} />}
            tickCount={5}
            tickMargin={10}
            width={yAxisWidth}
          />
          <Tooltip
            cursor={false}
            offset={20}
            wrapperStyle={{
              zIndex: tooltipZIndex,
              position: mode === ChartMode.Compact ? 'fixed' : 'absolute',
            }}
            allowEscapeViewBox={{ x: false, y: true }}
            content={(props: any) => {
              return isNil(tooltipProps) ? (
                <PopperPortal active={props.active}>
                  {CustomTooltip({
                    props,
                    lines: linesWithOthers,
                    chartValues: dataWithOthers,
                    linesMeta: linesMetaWithOthers,
                    timezone: orgTimezone,
                    forceBothValues: true,
                    displayedMeasures,
                    displayedSlices: displayedSlicesWithOthers,
                    chartType,
                    additionalLabel: builderInfoDisplay,
                    colorConfig,
                    tooltipPayloadContentRef: tooltipContentRef,
                    isBreakdownOverall,
                    chartNumberType,
                  })}
                </PopperPortal>
              ) : null;
            }}
          />
          {renderIncidents}
          {linesWithOthers.reverse().map((line) => {
            // if this is the base measure line and it's not the only visible line.
            // we don't display it on chart.
            if (
              !showMeasure &&
              ([
                line.measureKey,
                line.measureKey + ExpressionPostfixOnColumnName.percentKey,
              ].includes(line.name) ||
                line.name === mapComparisonKey(line.measureKey))
            ) {
              return null;
            }
            return (
              <Bar
                isAnimationActive={false}
                key={line.name}
                name={line.name}
                dataKey={line.name}
                fill={line.type === 'dashed' ? 'transparent' : line.color}
                stroke={line.color}
                stackId="a"
                minPointSize={BAR_CHART_MIN_POINT_SIZE}
                strokeWidth={BAR_CHART_STROKE_WIDTH}
                barSize={barSize}
                onMouseMove={(eventProps, _, mouseElement) => {
                  onBarMouseMove({
                    rechartProps: {
                      x: eventProps.x,
                      y: eventProps.y,
                      date: eventProps.date,
                      xAxisKey: DATA_KEY,
                    },
                    line,
                    mouseEvent: mouseElement,
                  });
                }}
                onMouseLeave={onMouseLeave}
                onClick={lockTooltipPositionHandler}
                shape={<StyledBar />}
              >
                {data.map((entry, index) => {
                  const opacity = getBarOpacity({
                    tooltipProps,
                    xAxisValue: entry[DATA_KEY] as string,
                    lineName: line.name,
                    isTooltipLocked,
                  });

                  return (
                    <Cell
                      key={`cell-${index}`}
                      opacity={opacity}
                      style={{
                        // there is no native way to hide null cell in <Bar/> @recharts
                        // using inline style to hide the cell as special case
                        display: entry[line.name] === null ? 'none' : 'block',
                      }}
                    />
                  );
                })}
              </Bar>
            );
          })}
          {renderCreateIncidentReferenceLine}
        </BarChart>
      </ResponsiveContainer>
      {tooltipProps && (
        <SingleChartDataTooltip
          analysisId={analysisId}
          handleCreateCohort={handleCreateCohortFromExpression}
          tooltipProps={tooltipProps}
          isTooltipLocked={isTooltipLocked}
          clearLockedTooltipProps={clearLockedTooltipProps}
          chartDataWithOthers={dataWithOthers}
          linesMetaWithOthers={linesMetaWithOthers}
        />
      )}
    </ResponsiveChartWrapper>
  );
});
